
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { reviews as allReviews } from '../../../mocks/reviews';

export default function TopReviews() {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.2 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.1 });

  // Get the 5 most recent reviews by date, falling back to array order
  const recentReviews = [...allReviews]
    .sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA;
    })
    .slice(0, 5);

  // Left column: reviews 0, 2, 4 (non-featured, smaller)
  const leftColumnReviews = [recentReviews[0], recentReviews[2], recentReviews[4]].filter(Boolean);
  // Right column: reviews 1, 3 (featured, taller)
  const rightColumnReviews = [recentReviews[1], recentReviews[3]].filter(Boolean);

  return (
    <section className="py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div 
            ref={leftRef}
            className="lg:col-span-5 space-y-6"
          >
            <div 
              className={`inline-block px-4 py-2 bg-[#D4AF37]/10 rounded-full transition-all duration-700 ${
                leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
            >
              <span className="text-[#D4AF37] font-semibold flex items-center gap-2">
                <span className="animate-sparkle">⭐</span> Editor's Choice
              </span>
            </div>
            <h2 
              className={`text-4xl lg:text-5xl font-bold text-[#1B4332] transition-all duration-700 delay-100 ${
                leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Top-Rated Gift Reviews
            </h2>
            <p 
              className={`text-xl text-gray-600 leading-relaxed transition-all duration-700 delay-200 ${
                leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Our expert team tests and reviews hundreds of products to bring you honest, detailed insights. Every recommendation is backed by real-world testing and careful evaluation.
            </p>
            <div className="space-y-4">
              {[
                { icon: 'ri-checkbox-circle-fill', color: 'bg-[#1B4332]', title: 'Thoroughly Tested', desc: 'Every product is personally tested by our team' },
                { icon: 'ri-heart-fill', color: 'bg-[#8B2635]', title: 'Unbiased Opinions', desc: 'Honest reviews with real pros and cons' },
                { icon: 'ri-star-fill', color: 'bg-[#D4AF37]', title: 'Expert Insights', desc: 'Detailed analysis to help you decide' }
              ].map((item, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-4 group cursor-pointer transition-all duration-700 ${
                    leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                  }`}
                  style={{ transitionDelay: `${300 + index * 100}ms` }}
                >
                  <div className={`w-12 h-12 flex items-center justify-center ${item.color} rounded-full flex-shrink-0 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg`}>
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1B4332] mb-1 group-hover:text-[#8B2635] transition-colors">{item.title}</h4>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link 
              to="/reviews"
              className={`inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#E5C158] text-white rounded-full font-semibold hover:shadow-xl hover:shadow-[#D4AF37]/30 transition-all duration-300 hover:scale-105 whitespace-nowrap group btn-festive ${
                leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: '600ms' }}
            >
              See All Reviews
              <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
            </Link>
          </div>

          <div ref={rightRef} className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Left column - 3 smaller cards */}
              <div className="space-y-6">
                {leftColumnReviews.map((review, index) => (
                  <Link
                    key={review.id}
                    to={`/reviews/${review.id}`}
                    className={`block bg-white rounded-2xl overflow-hidden shadow-lg card-festive group transition-all duration-700 ${
                      rightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                    }`}
                    style={{ transitionDelay: `${index * 150}ms` }}
                  >
                    <div className="w-full h-52 overflow-hidden relative">
                      <img 
                        src={review.heroImage || review.image} 
                        alt={review.name}
                        className="w-full h-full object-contain bg-white group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="p-6">
                      <div className="inline-block px-3 py-1 bg-[#1B4332]/5 rounded-full mb-3 group-hover:bg-[#1B4332]/10 transition-colors">
                        <span className="text-[#1B4332] text-xs font-semibold">{review.category}</span>
                      </div>
                      <h3 className="font-bold text-[#1B4332] text-lg mb-2 group-hover:text-[#8B2635] transition-colors line-clamp-2">
                        {review.name}
                      </h3>
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <i 
                            key={i} 
                            className={`ri-star-fill text-sm transition-transform duration-300 ${i < Math.round(review.rating) ? 'text-[#D4AF37]' : 'text-gray-300'}`}
                            style={{ transitionDelay: `${i * 50}ms` }}
                          ></i>
                        ))}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#1B4332] font-medium group-hover:gap-2 flex items-center gap-1 transition-all">
                          Read Review
                          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Right column - 2 featured taller cards */}
              <div className="space-y-6 sm:pt-12">
                {rightColumnReviews.map((review, index) => (
                  <Link
                    key={review.id}
                    to={`/reviews/${review.id}`}
                    className={`block bg-white rounded-2xl overflow-hidden shadow-lg card-festive group transition-all duration-700 ${
                      rightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                    }`}
                    style={{ transitionDelay: `${(index + 3) * 150}ms` }}
                  >
                    <div className="relative w-full h-64 overflow-hidden">
                      <img 
                        src={review.heroImage || review.image} 
                        alt={review.name}
                        className="w-full h-full object-contain bg-white group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute top-4 right-4 px-3 py-1 bg-[#D4AF37] rounded-full animate-pulse-glow">
                        <span className="text-white text-xs font-bold">FEATURED</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="inline-block px-3 py-1 bg-[#1B4332]/5 rounded-full mb-3 group-hover:bg-[#1B4332]/10 transition-colors">
                        <span className="text-[#1B4332] text-xs font-semibold">{review.category}</span>
                      </div>
                      <h3 className="font-bold text-[#1B4332] text-xl mb-2 group-hover:text-[#8B2635] transition-colors line-clamp-2">
                        {review.name}
                      </h3>
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <i 
                            key={i} 
                            className={`ri-star-fill transition-all duration-300 group-hover:scale-110 ${i < Math.round(review.rating) ? 'text-[#D4AF37]' : 'text-gray-300'}`}
                            style={{ transitionDelay: `${i * 50}ms` }}
                          ></i>
                        ))}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#1B4332] font-medium group-hover:gap-2 flex items-center gap-1 transition-all">
                          Read Review
                          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
